import { keyBy, isEmpty } from "lodash"
import { sanitizePositionSet } from "../helpers"
import {
  getLatestData,
  createKeyedContributions,
  groupFactorsByCategory,
  recursiveMapFactorValues,
  RISK_NON_AGGREGABLE_KEYS,
  FACTOR_METADATA_SUBQUERY,
} from "./helpers/factor-utils"

const RISK_SUBQUERY = `
  total
  attribution {
    summary {
      factors
      specific
    }
    factors {
      ${FACTOR_METADATA_SUBQUERY}
      value
    }
  }
`

const FACTOR_RISK_SUBQUERY = `
  factors {
    ${FACTOR_METADATA_SUBQUERY}
    risk (from: $from, to: $to, type: PREDICTED, interval: AUTO) {
      value
    }
  }
`

const getQuery = (shouldUseIntradayQuery) => {
  const innerQuery = `
    risk{
        ${RISK_SUBQUERY}
    }
    valueAtRisk(tradingDaysHorizon: $horizon, confidence: $confidence){
        ${RISK_SUBQUERY}
    }
    exposure {
        factors {
        id
        name
        currencyExposure
        net
        category
        }
    }
  `

  // The factors query outside of intraday is used for drift computation
  // We are using the from/to of the selected period, where TO is yesterday's date
  // Since we're using yesterday's risk data as a proxy to compute risk for intraday, the drift should
  // be roughly the same.
  if (shouldUseIntradayQuery) {
    return `
      query ($modelId: String!, $positionSet: PositionSetInput!, $on: Date!, $batchId: Id, $from: Date!, $to: Date!, $horizon: PositiveInt!, $confidence: Float!) {
        model(id: $modelId) {
          ${FACTOR_RISK_SUBQUERY}
          simulation:intradaySimulation(positionSet: $positionSet, on: $on, batchId: $batchId) {
            ${innerQuery}
          }
        }
      }
   `
  }

  return `
    query ($modelId: String!, $positionSet: PositionSetInput!, $from: Date!, $to: Date!, $base: [PositionSetInput], $horizon: PositiveInt!, $confidence: Float!) {
      model(id: $modelId) {
        ${FACTOR_RISK_SUBQUERY}
        simulation(positionSet: $positionSet, from: $from, to: $to, base: $base, interval: AUTO) {
          ${innerQuery}
        }
      }
    }
  `
}

function getGroupedFactors(factors, compositionByCategory, prefix = "risk") {
  if (isEmpty(factors)) {
    return {}
  }
  const customMappingFn = ({ values: factorValues, groupedFactors, factorCategory }) => {
    const values = recursiveMapFactorValues(
      factorValues,
      groupedFactors,
      factorCategory,
      RISK_NON_AGGREGABLE_KEYS,
    )

    if (compositionByCategory[factorCategory]) {
      return {
        ...values,
        [prefix]: {
          ...values[prefix],
          ...compositionByCategory[factorCategory].reduce((acc, value) => {
            acc.riskDecompositionContribution = [...acc.riskDecompositionContribution, value.riskDecomposition]
            acc.exposureContribution = [...acc.exposureContribution, value.exposureContribution]
            acc.predictedRiskContribution = [...acc.predictedRiskContribution, value.predictedRisk]
            return acc
          }, { riskDecompositionContribution: [], exposureContribution: [], predictedRiskContribution: [] }),
        },
      }
    }

    return values
  }

  return groupFactorsByCategory(factors, RISK_NON_AGGREGABLE_KEYS, false, customMappingFn)
}

export function buildFactorData(prefix, factorRisk, factor, factorExposure, factorValueAtRisk, riskContribution, exposureContribution, netExposureContribution) {
  if (!factor) {
    return null
  }

  const predictedRiskContribution = factor.risk
  const latestPredictedRisk = getLatestData(predictedRiskContribution)

  return {
    id: factor.id || factorRisk?.id,
    isFactor: true,
    label: factor.name,
    category: factor.category,
    values: {
      [prefix]: {
        riskDecomposition: factorRisk?.value,
        riskDecompositionContribution: riskContribution,
        exposure: factorExposure?.currencyExposure,
        exposureContribution: exposureContribution,
        netExposure: factorExposure?.net,
        netExposureContribution: netExposureContribution,
        predictedRisk: latestPredictedRisk?.value ? latestPredictedRisk.value * 100 : undefined,
        predictedRiskContribution,
        valueAtRisk: factorValueAtRisk?.value,
      },
    },
  }
}

export function combineFactors(prefix, risk, exposure, valueAtRisk, factorsResults) {
  if (!risk || !exposure || !valueAtRisk || !factorsResults) {
    return {}
  }

  const latestExposureFactors = getLatestData(exposure, "factors")

  // If a position set is not exposed to any factors, the set will not be exposed to any factor risk
  if (isEmpty(latestExposureFactors)) {
    return {}
  }

  const latestRiskFactors = getLatestData(risk, "attribution.factors")
  const latestValueAtRiskFactors = getLatestData(valueAtRisk, "attribution.factors")

  if (!latestRiskFactors) {
    return {}
  }

  // Pre-compute keyed lookups for better performance
  const keyedExposureFactors = keyBy(latestExposureFactors, "id")
  const keyedValueAtRiskFactors = keyBy(latestValueAtRiskFactors || [], "id")
  const keyedFactorsResults = keyBy(factorsResults, "id")

  // Pre-compute contributions
  const riskContributions = createKeyedContributions(risk, "attribution.factors", "value")
  const exposureContributions = createKeyedContributions(exposure, "factors", "currencyExposure")
  const netExposureContributions = createKeyedContributions(exposure, "factors", "net")

  const compositionByCategory = {}

  const factorsData = latestRiskFactors.reduce((combinedFactors, factorRisk) => {
    const factorId = factorRisk.id
    const factorExposure = keyedExposureFactors[factorId]
    const factorValueAtRisk = keyedValueAtRiskFactors[factorId]
    const factor = keyedFactorsResults[factorId]

    if (!factor) {
      return combinedFactors
    }

    const riskContribution = riskContributions.map(date => date[factorId] || { value: undefined })
    const exposureContribution = exposureContributions.map(date => date[factorId] || { value: undefined })
    compositionByCategory[factor.category] = factor.risk.reduce((acc, predictedRiskFactor, index) => {
      const riskDecompValue = riskContribution[index]?.value
      const exposureValue = exposureContribution[index]?.value
      const predictedValue = predictedRiskFactor?.value

      if (!acc[index]) {
        acc[index] = {
          predictedRiskContribution: predictedValue,
          riskDecomposition: riskDecompValue,
          exposureContribution: exposureValue,
        }
      } else {
        acc[index].riskDecomposition += riskDecompValue
        acc[index].predictedRiskContribution += predictedValue
        acc[index].exposureContribution += exposureValue
      }

      return acc
    }, compositionByCategory[factor.category] || [])

    const factorData = buildFactorData(
      prefix,
      factorRisk,
      factor,
      factorExposure,
      factorValueAtRisk,
      riskContribution,
      exposureContribution,
      netExposureContributions.map(date => date[factorId] || { value: undefined }),
    )

    if (factorData) {
      combinedFactors[factorId] = factorData
    }

    return combinedFactors
  }, {})

  return { factorsData, compositionByCategory }
}

export function createRiskFactorsQuery({ prefix, modelId, positionSet, base, from, to, on, batchId, shouldUseIntradayQuery }) {
  const sanitizedPositionSet = sanitizePositionSet(positionSet)

  return {
    prefix,
    key: ["risk", "factors", prefix, modelId, positionSet, base, from, to, shouldUseIntradayQuery, on, batchId],
    query: getQuery(shouldUseIntradayQuery),
    variables: {
      from,
      to,
      base,
      modelId,
      positionSet: sanitizedPositionSet,
      horizon: 252,
      confidence: 84,
      batchId,
      on,
    },
    select: (result) => {
      const simulation = result.data.model.simulation
      const exposure = simulation?.exposure
      const risk = simulation?.risk
      const valueAtRisk = simulation?.valueAtRisk
      const factorsResults = result.data.model.factors

      const { factorsData, compositionByCategory } = combineFactors(prefix, risk, exposure, valueAtRisk, factorsResults)

      const latestRiskAttributionSummary = risk?.at(-1)?.attribution.summary || {}
      const latestValueAtRiskAttributionSummary = valueAtRisk?.at(-1)?.attribution.summary || {}

      const specific = {
        label: "Specific",
        values: {
          [prefix]: {
            riskDecomposition: latestRiskAttributionSummary.specific,
            currencyPredictedRisk: latestValueAtRiskAttributionSummary.specific,
          },
        },
        pinnedRowOrder: 1,
      }

      const factors = {
        label: "Factors",
        values: {
          [prefix]: {
            riskDecomposition: latestRiskAttributionSummary.factors,
            currencyPredictedRisk: latestValueAtRiskAttributionSummary.factors,
          },
        },
        children: factorsData,
        pinnedRowOrder: 2,
      }

      return {
        children: {
          Specific: specific,
          Factors: factors,
          ...getGroupedFactors(factorsData, compositionByCategory, prefix),
        },
      }
    },
  }
}
